import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
export function WorkRelationshipDecodeDetail() {
  const reportData = {
    name: 'Work Relationship Decode',
    estimatedTime: '4–5 minutes',
    depth: 'Full diagnostic (L2–L4)',
    price: '₹999',
    analyses: ['Authority and peer dynamic mapping', 'Adaptive response patterns in work relationships', 'Implicit risk perception and constraint loops', 'Outcome patterns and relationship sustainability'],
    receives: [{
      level: 'L2',
      description: 'Work relationship mechanics and response loops'
    }, {
      level: 'L3',
      description: 'Power and peer perception patterns'
    }, {
      level: 'L4',
      description: 'Constraint points and leverage opportunities'
    }, {
      level: 'Optional',
      description: 'Cross-context work relationship implications'
    }],
    doesNotDo: ['Does not evaluate competence or performance', 'Does not assign personality types or labels', 'Does not provide advice or action plans', 'Does not replace coaching or conflict resolution'],
    inputsRequired: {
      baseline: false,
      reportSpecific: 'Work relationship context and dynamic-specific questions',
      estimatedInputTime: '4–5 minutes'
    }
  };
  const handleUnlockReport = () => {
    // Store selected report type (Class B - no baseline required)
    localStorage.setItem('selectedReport', 'work-relationship-decode');
  };
  return <>
      <Helmet data-id="element-5384">
        <title data-id="element-5385">{reportData.name} — Decode | Lazy Monk Labs</title>
        <meta name="description" content={`Full diagnostic report: ${reportData.name}`} data-id="element-5386" />
        <meta name="robots" content="noindex, nofollow" data-id="element-5387" />
        <link rel="canonical" href="https://lazymonklabs.com/work-relationship-decode-detail" data-id="element-5388" />

        {/* Google Analytics */}
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-QCXDHW3F0P" data-id="element-5389"></script>
        <script data-id="element-5390">
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-QCXDHW3F0P');
          `}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-5391">
        {/* Navigation */}
        <header data-id="element-5392">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-5393">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-5394">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-5395">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-5396" />
                <span className="text-lg font-medium tracking-tight" data-id="element-5397">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-5398">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-5399">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-5400">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="pt-32 pb-24 px-6" data-id="element-5401">
          <div className="max-w-3xl mx-auto" data-id="element-5402">
            {/* Page Header */}
            <div className="mb-16" data-id="element-5403">
              <h1 className="text-4xl md:text-5xl font-medium mb-3 leading-tight tracking-tight" data-id="element-5404">
                {reportData.name}
              </h1>
              <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-5405">
                Full diagnostic report
              </p>
              <div className="flex flex-col sm:flex-row gap-4 text-xs text-[#F5F5F5]/40" data-id="element-5406">
                <span data-id="element-5407">
                  Estimated time to complete: {reportData.estimatedTime}
                </span>
                <span className="hidden sm:inline" data-id="element-5408">·</span>
                <span data-id="element-5409">Report depth: {reportData.depth}</span>
              </div>
            </div>

            {/* Section 1: What This Report Analyzes */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5410">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-5411">
                What this report analyzes
              </h2>
              <ul className="space-y-4" data-id="element-5412">
                {reportData.analyses.map((item, index) => <li key={index} className="flex gap-4" data-id="element-5413">
                    <span className="text-[#F5F5F5]/30 mt-1" data-id="element-5414">•</span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-5415">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </section>

            {/* Section 2: What You'll Receive */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5416">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-5417">
                What you'll receive
              </h2>
              <div className="space-y-4" data-id="element-5418">
                {reportData.receives.map((item, index) => <div key={index} className="flex gap-4" data-id="element-5419">
                    <span className="text-[#F5F5F5]/50 font-medium text-sm" data-id="element-5420">
                      {item.level}
                    </span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-5421">
                      {item.description}
                    </span>
                  </div>)}
              </div>
            </section>

            {/* Section 3: What This Report Does Not Do */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5422">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-5423">
                What this report does not do
              </h2>
              <ul className="space-y-4" data-id="element-5424">
                {reportData.doesNotDo.map((item, index) => <li key={index} className="flex gap-4" data-id="element-5425">
                    <span className="text-[#F5F5F5]/30 mt-1" data-id="element-5426">•</span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-5427">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </section>

            {/* Section 4: Inputs Required */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-5428">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-5429">
                Inputs required
              </h2>
              <div className="space-y-6" data-id="element-5430">
                {!reportData.inputsRequired.baseline && <div data-id="element-5431">
                    <p className="text-[#F5F5F5]/70 mb-2" data-id="element-5432">
                      No baseline setup required for this report.
                    </p>
                  </div>}
                <div data-id="element-5433">
                  <p className="text-[#F5F5F5]/70 mb-2" data-id="element-5434">
                    {reportData.inputsRequired.reportSpecific}
                  </p>
                </div>
                <div data-id="element-5435">
                  <p className="text-xs text-[#F5F5F5]/50" data-id="element-5436">
                    Estimated input time:{' '}
                    {reportData.inputsRequired.estimatedInputTime}
                  </p>
                </div>
              </div>
            </section>

            {/* Section 5: Price & Purchase */}
            <section className="mb-12" data-id="element-5437">
              <div className="border border-[#2B2B2B] p-8" data-id="element-5438">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" data-id="element-5439">
                  <div data-id="element-5440">
                    <p className="text-3xl font-medium mb-2" data-id="element-5441">
                      {reportData.price}
                    </p>
                    <p className="text-xs text-[#F5F5F5]/50" data-id="element-5442">
                      One-time purchase · No subscription
                    </p>
                  </div>
                  <Link to="/login" onClick={handleUnlockReport} className="inline-block px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-center font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg whitespace-nowrap" data-id="element-5443">
                    Unlock Full Report
                  </Link>
                </div>
              </div>
            </section>
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-5444">
          <div className="max-w-7xl mx-auto text-center" data-id="element-5445">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-5446">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}